import { FormCtor, Form } from './Form';
import type { FormComponent } from './Form';
import FormButtons from './FormButtons';
import { LabelCtor, Label } from './Label';
import { TextBoxCtor, TextBox, TextBoxButton } from './TextBox';
import { CheckBoxCtor, CheckBox } from './CheckBox';
import { PriceBoxCtor, PriceBox } from './PriceBox';
import { IntBoxCtor, IntBox } from './IntBox';
import { QuantityBoxCtor, QuantityBox } from './QuantityBox';
import { DecimalBoxCtor, DecimalBox } from './DecimalBox';
import { DateTimeBoxCtor, DateTimeBox } from './DateTimeBox';
import { TimePickerCtor, TimePicker } from './TimePicker';
import ColorIndicator from './ColorIndicator';
import { DatePickerCtor, DatePicker } from './DatePicker';
import { ComboBoxCtor, ComboBox } from './ComboBox';
import ErrorBlock from './ErrorBlock';
import BlockingOverlay from './BlockingOverlay';
import Spinner from './Spinner';
import Ellipsis from './Ellipsis';
import AutoCompleteTextBox from './AutoCompleteTextBox';

import { DataGridCtor, DataGrid } from './DataGrid';
import type { DataGridColumn } from './DataGrid.Column';
import type { ColumnFilter } from './DataGrid.Filter';
import type { SortDirection } from './DataGrid.SortHeaderCell';
import { buildCellToolbar } from './DataGrid.CellToolbar';

import { NumericDataGridCtor, NumericDataGrid } from './NumericDataGrid';

import { TextDataGridCtor, TextDataGrid } from './TextDataGrid';

import * as Format from './Format';
import * as ImageUtils from './ImageUtils';

import Panel from './Panel';
import PanelStripe from './PanelStripe';
import Button from './Button';
import { GridButton } from './GridButton';
import { Row, Column } from './Grid';
import type { ColumnSize } from './Grid';
import { Dialog, DialogActions, DialogComponentProps, showConfirmation, ifConfirm, showCustom, showScreen, showMessage, DialogBody, DialogFooter, DialogHeader } from './Dialog';
import { ColorPickerCtor, ColorPicker } from './ColorPicker';
import { ImageSelectorCtor, ImageSelector } from './ImageSelector';
import { NativeDatePicker, NativeDatePickerCtor } from './NativeDatePicker';
import { MultiSelectInputCtor, MultiSelectInput } from './MultiSelectInput';
import { MultiSelectListCtor, MultiSelectList } from './MultiSelectList';
import * as ResizeListener from './ResizeListener';
import { RadioButtonGroupCtor, RadioButtonGroup, RadioButtonCtor, RadioButton } from './RadioButton';
import { whenEnterKey, whenEscKey, debounce, addLineBreaks, addShortcuts, sendFocusToInitialElement, formatAsElementId } from './Utils';
import { TimeBoxCtor, TimeBox } from './TimeBox';
import { EditableValueListCtor, EditableValueList } from './EditableValueList';
import { EditableStringListCtor, EditableStringList } from './EditableStringList';
import { SelectInput } from './SelectInput';
import type { SelectInputCtor } from './SelectInput';
import NestedList from './NestedList';
import { FileSelector, FileSelectorCtor } from './FileSelector';
import Chart from './Chart';
import LoadingBar from './LoadingBar';
import { CodeEditor, CodeEditorCtor } from './CodeEditor';
import { HtmlEditor, HtmlEditorCtor } from './HtmlEditor';

import * as Excel from './DataGrid.ExcelExport';
import * as Constraints from './Constraints';
import * as Raw from './raw';
import { StyleButtonCtor, StyleButton, type StyleButtonType } from './StyleButton';
import type { StyleButtonMask } from './StyleButton';

import * as ScreenUtils from './ScreenUtils';
import { TranslatableTextBox, TranslatableTextBoxCtor } from './TranslatableTextBox';
import { RemoteMultiSelectList, RemoteMultiSelectListCtor } from './RemoteMultiSelectList';
import { RemoteMultiSelectNumericList, RemoteMultiSelectNumericListCtor } from './RemoteMultiSelectNumericList';

function controlsFor<T>() {

	const form: FormCtor<T> = Form;
	const label: LabelCtor<T> = Label;
	const textBox: TextBoxCtor<T> = TextBox;
	const checkBox: CheckBoxCtor<T> = CheckBox;
	const dataGrid: DataGridCtor<T> = DataGrid;
	const numericDataGrid: NumericDataGridCtor<T> = NumericDataGrid;
	const textDataGrid: TextDataGridCtor<T> = TextDataGrid;
	const datePicker: DatePickerCtor<T> = DatePicker;
	const timePicker: TimePickerCtor<T> = TimePicker;
	const comboBox: ComboBoxCtor<T> = ComboBox;
	const colorPicker: ColorPickerCtor<T> = ColorPicker;
	const imageSelector: ImageSelectorCtor<T> = ImageSelector;
	const fileSelector: FileSelectorCtor<T> = FileSelector;
	const multiSelectInput: MultiSelectInputCtor<T> = MultiSelectInput;
	const multiSelectList: MultiSelectListCtor<T> = MultiSelectList;
	const remoteMultiSelectList: RemoteMultiSelectListCtor<T> = RemoteMultiSelectList;
	const remoteMultiSelectNumericList: RemoteMultiSelectNumericListCtor<T> = RemoteMultiSelectNumericList;
	const radioButtonGroup: RadioButtonGroupCtor<T> = RadioButtonGroup;
	const radioButton: RadioButtonCtor<T> = RadioButton;
	const timeBox: TimeBoxCtor<T> = TimeBox;
	const editableValueList: EditableValueListCtor<T> = EditableValueList;
	const editableStringList: EditableStringListCtor<T> = EditableStringList;
	const priceBox: PriceBoxCtor<T> = PriceBox;
	const intBox: IntBoxCtor<T> = IntBox;
	const quantityBox: QuantityBoxCtor<T> = QuantityBox;
	const decimalBox: DecimalBoxCtor<T> = DecimalBox;
	const dateTimeBox: DateTimeBoxCtor<T> = DateTimeBox;
	const translatableBox: TranslatableTextBoxCtor<T> = TranslatableTextBox;
	const codeEditor: CodeEditorCtor<T> = CodeEditor;
	const styleButton: StyleButtonCtor<T> = StyleButton;
	const htmlEditor: HtmlEditorCtor<T> = HtmlEditor;
	const nativeDatePicker: NativeDatePickerCtor<T> = NativeDatePicker;

	return {
		Form: form,
		Label: label,
		TextBox: textBox,
		CheckBox: checkBox,
		DataGrid: dataGrid,
		DatePicker: datePicker,
		TimePicker: timePicker,
		ComboBox: comboBox,
		ColorPicker: colorPicker,
		ImageSelector: imageSelector,
		MultiSelectInput: multiSelectInput,
		MultiSelectList: multiSelectList,
		RadioButtonGroup: radioButtonGroup,
		RadioButton: radioButton,
		TimeBox: timeBox,
		EditableValueList: editableValueList,
		EditableStringList: editableStringList,
		PriceBox: priceBox,
		IntBox: intBox,
		QuantityBox: quantityBox,
		DecimalBox: decimalBox,
		NumericDataGrid: numericDataGrid,
		TextDataGrid: textDataGrid,
		FileSelector: fileSelector,
		DateTimeBox: dateTimeBox,
		CodeEditor: codeEditor,
		StyleButton: styleButton,
		HtmlEditor: htmlEditor,
		TranslatableTextBox: translatableBox,
		NativeDatePicker: nativeDatePicker,
		RemoteMultiSelectList: remoteMultiSelectList,
		RemoteMultiSelectNumericList: remoteMultiSelectNumericList
	};
}

const Modal = {
	showConfirmation,
	ifConfirm,
	showCustom,
	showScreen,
	showMessage
};

export {
	controlsFor,
	whenEnterKey,
	whenEscKey,

	debounce,

	addLineBreaks,

	Format,
	Panel,
	PanelStripe,
	Label,
	TranslatableTextBox,
	PriceBox,
	IntBox,
	QuantityBox,
	DecimalBox,
	TextBox,
	TextBoxButton,
	FormButtons,
	Form,
	FormComponent,
	CheckBox,
	ComboBox,
	MultiSelectInput,
	MultiSelectList,
	DatePicker,
	NativeDatePicker,
	Row,
	Column,
	ColumnSize,
	DataGrid,
	DataGridColumn,
	ColumnFilter,
	NumericDataGrid,
	TextDataGrid,
	SortDirection,
	buildCellToolbar,
	Button,
	GridButton,
	Dialog,
	DialogFooter,
	DialogHeader,
	DialogBody,
	DialogActions, Modal,
	ColorPicker,
	ImageSelector,
	RadioButtonGroup,
	RadioButton,
	ResizeListener,
	TimeBox,
	DateTimeBox,
	Constraints,
	EditableValueList,
	EditableStringList,
	SelectInput,
	SelectInputCtor,
	ErrorBlock,
	NestedList,
	ImageUtils,
	FileSelector,
	Chart,
	AutoCompleteTextBox,
	Raw,
	ColorIndicator,
	Excel,
	CodeEditor,
	LoadingBar,
	StyleButton,
	BlockingOverlay,
	Spinner,
	Ellipsis,
	showCustom,
	addShortcuts,
	sendFocusToInitialElement,
	ScreenUtils,
	HtmlEditor,
	RemoteMultiSelectList,
	formatAsElementId,
	type StyleButtonType,
	type StyleButtonMask
};

export type { DialogComponentProps };

export * as TagUtils from './TagUtils';
export * from './createTagsFilter';
export * as BarcodeReader from './BarcodeReader';
